// Sidechain ducking — amplitude-follow one signal (the "key", typically
// a kick drum) and use its envelope to attenuate another signal (the
// "source", typically a bass or pad).
//
// Pure Float32 math, no Web Audio nodes. The envelope is RMS over a
// short window, then smoothed by a single-pole release filter so the
// duck releases over a configurable time instead of snapping back.

import { toMono, type PcmBuffer } from '../../pipeline';

export type SidechainOptions = {
  /** 0 = no ducking, 1 = full ducking at the envelope's peak. */
  amount: number;
  /** Smoothing time after the envelope falls. Higher = slower release. */
  releaseMs: number;
  /** RMS window for the envelope follower. Default 30ms. */
  envelopeMs?: number;
};

export function applySidechain(
  source: PcmBuffer,
  key: PcmBuffer,
  opts: SidechainOptions,
): PcmBuffer {
  const amount = clamp01(opts.amount);
  const releaseMs = Math.max(0, opts.releaseMs);
  const envelopeMs = opts.envelopeMs ?? 30;

  // Mono-sum the key signal once — sidechain doesn't care about stereo
  // positioning of the key, only its loudness over time.
  const keyMono = toMono(key).channels[0]!;
  const keyEnv = rmsEnvelope(keyMono, key.sampleRate, envelopeMs);

  // Resize key envelope to match source length so we can index 1:1.
  // Resampling is linear interpolation — coarse but inaudible because the
  // envelope is already smooth (RMS + release filter).
  const srcLen = source.channels[0]?.length ?? 0;
  const stretched = resampleLinear(keyEnv, srcLen);

  // Normalise so the loudest envelope sample maps to 1.0 — otherwise
  // "amount = 1" depends on the absolute level of the key signal.
  let peak = 0;
  for (let i = 0; i < stretched.length; i++) {
    if (stretched[i]! > peak) peak = stretched[i]!;
  }
  if (peak > 0) {
    for (let i = 0; i < stretched.length; i++) stretched[i] = stretched[i]! / peak;
  }

  // Single-pole release. Attack is unfiltered (instant duck), release is
  // exponential — matches how a real sidechain compressor behaves.
  const releaseCoeff = releaseMs > 0
    ? Math.exp(-1 / ((releaseMs / 1000) * source.sampleRate))
    : 0;
  let env = 0;
  const gain = new Float32Array(srcLen);
  for (let i = 0; i < srcLen; i++) {
    const target = stretched[i]!;
    if (target > env) env = target; // attack: instant
    else env = target + (env - target) * releaseCoeff; // release: filtered
    gain[i] = 1 - env * amount;
  }

  const channels = source.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    for (let i = 0; i < ch.length; i++) dst[i] = ch[i]! * gain[i]!;
    return dst;
  });
  return { sampleRate: source.sampleRate, channels };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function rmsEnvelope(
  channel: Float32Array,
  sampleRate: number,
  windowMs: number,
): Float32Array {
  const window = Math.max(8, Math.round((windowMs / 1000) * sampleRate));
  const out = new Float32Array(channel.length);
  // Running window sum of squares for cheap RMS.
  let sumSq = 0;
  for (let i = 0; i < channel.length; i++) {
    const inV = channel[i]! ** 2;
    sumSq += inV;
    if (i >= window) sumSq -= channel[i - window]! ** 2;
    const denom = Math.min(i + 1, window);
    out[i] = Math.sqrt(sumSq / denom);
  }
  return out;
}

function resampleLinear(src: Float32Array, outLen: number): Float32Array {
  const out = new Float32Array(outLen);
  if (src.length === 0 || outLen === 0) return out;
  if (src.length === outLen) {
    out.set(src);
    return out;
  }
  const ratio = (src.length - 1) / Math.max(1, outLen - 1);
  for (let i = 0; i < outLen; i++) {
    const t = i * ratio;
    const i0 = Math.floor(t);
    const frac = t - i0;
    const a = src[i0] ?? 0;
    const b = src[Math.min(src.length - 1, i0 + 1)] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}
